import React, { useState } from 'react';
import { LivePage, FilterBar, DataTable, StatusBadge, Age, Code, assertExecOk, execOk, execOutput, clustersAPI , useNamespaces, NamespaceSelect } from './LivePage';
import { useCluster } from '../context/ClusterContext';
import { useAuth } from '../context/AuthContext';

const lbl = (text) => <label style={{ fontSize:12, fontWeight:600, color:'var(--ink-2)', display:'block', marginBottom:4 }}>{text}</label>;

function CreatePVCModal({ clusterId, initialNamespace = 'default', namespaces = [], onClose, onCreated }) {
  const [form, setForm] = useState({
    name:'', namespace: initialNamespace, size:'5', storageClass:'local-path', accessMode:'ReadWriteOnce',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const setF = (k,v) => setForm(f=>({...f,[k]:v}));

  const handleCreate = async () => {
    if (!form.name) { setError('Name is required'); return; }
    setSaving(true); setError('');
    try {
      const yaml = `apiVersion: v1
kind: PersistentVolumeClaim
metadata:
  name: ${form.name}
  namespace: ${form.namespace}
spec:
  accessModes:
  - ${form.accessMode}
  storageClassName: ${form.storageClass}
  resources:
    requests:
      storage: ${form.size}Gi`;
      const cmd = `kubectl create namespace ${form.namespace} --dry-run=client -o yaml | kubectl apply -f - 2>/dev/null; echo '${yaml}' | kubectl apply -f -`;
      const r = await clustersAPI.exec(clusterId, cmd, { section: 'storage', action: 'create' });
      // Check stdout AND stderr — a failed apply (RBAC, read-only agent, bad
      // storageClass) comes back on stderr at HTTP 200, so checking only stdout
      // reported phantom success and the PVC never appeared in the list.
      if (execOk(r)) { onCreated(); onClose(); }
      else setError(execOutput(r) || 'Create failed (no output from cluster — is the agent connected with write scope?)');
    } catch (e) { setError(e.response?.data?.detail || 'Create failed'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000,
      display:'flex', alignItems:'center', justifyContent:'center' }}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="card" style={{ width:460 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <h2 style={{ margin:0, fontSize:16, fontWeight:700 }}>Create PVC</h2>
          <button onClick={onClose} className="btn btn-ghost btn-icon" style={{ fontSize:20 }}>✕</button>
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>{lbl('Name *')}<input placeholder="my-pvc" value={form.name} onChange={e=>setF('name',e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,'-'))} className="input" /></div>
            <div>{lbl('Namespace')}<NamespaceSelect namespaces={namespaces} ns={form.namespace} onChange={v=>setF('namespace',v)} /></div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              {lbl('Size (GB)')}
              <input type="number" min="1" placeholder="5" value={form.size} onChange={e=>setF('size',e.target.value)} className="input" />
            </div>
            <div>
              {lbl('Storage Class')}
              <input placeholder="local-path" value={form.storageClass} onChange={e=>setF('storageClass',e.target.value)} className="input" />
            </div>
          </div>
          <div>
            {lbl('Access Mode')}
            <select value={form.accessMode} onChange={e=>setF('accessMode',e.target.value)} className="select">
              <option value="ReadWriteOnce">ReadWriteOnce — single node read/write</option>
              <option value="ReadOnlyMany">ReadOnlyMany — multiple nodes read</option>
              <option value="ReadWriteMany">ReadWriteMany — multiple nodes read/write</option>
            </select>
          </div>
          <div style={{ padding:'8px 12px', background:'var(--surface-2)', borderRadius:6, fontSize:12, color:'var(--muted)' }}>
            PVC will request <strong>{form.size}Gi</strong> from <strong>{form.storageClass}</strong> storage class.
            For <code>local-path</code>, binding happens when a pod using this PVC is scheduled.
          </div>
          {error && <div className="banner danger">{error}</div>}
          <div style={{ display:'flex', gap:8, marginTop:4 }}>
            <button onClick={handleCreate} disabled={saving} className="btn btn-primary" style={{ flex:1 }}>
              {saving ? '⟳ Creating...' : '+ Create PVC'}
            </button>
            <button onClick={onClose} className="btn btn-secondary">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PVCs() {
  const { hasSectionAction } = useAuth();
  const { selectedCluster } = useCluster();
  // SARVKUBE-65: the cluster's REAL namespace list, so the dropdown offers namespaces whose
  // objects are not currently displayed. Built from loaded rows before, which made it
  // impossible to reach a namespace that was not already on screen.
  const { namespaces: allNamespaces } = useNamespaces(selectedCluster?.id);
  const canCreate = hasSectionAction('storage', 'create');
  const canDelete = hasSectionAction('storage', 'delete');
  const [nsFilter, setNsFilter]     = useState('all');
  const [nameFilter, setNameFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  return (
    <LivePage deps={[nsFilter]} title="Persistent Volume Claims" fetchFn={id => clustersAPI.getWorkloadPaged('persistentvolumeclaims', id, { pageSize: 500, namespace: nsFilter === 'all' ? undefined : nsFilter })}>
      {(data, refresh, clusterId) => {
        // Agent clusters return a raw K8s List (mixed items); normalize PersistentVolumeClaim items.
        const _raw = data.pvcs || (data.items || []);
        const items = _raw
          .filter(it => (it.kind || 'PersistentVolumeClaim') === 'PersistentVolumeClaim')
          .map(it => it.metadata ? {
            name: it.metadata.name,
            namespace: it.metadata.namespace,
            status: it.status?.phase || 'Unknown',
            capacity: (it.status?.capacity || {}).storage || (it.spec?.resources?.requests || {}).storage || '',
            access_modes: (it.spec?.accessModes || []).join(','),
            storage_class: it.spec?.storageClassName || '',
            created_at: it.metadata.creationTimestamp,
          } : it);
        const namespaces = ['all', ...allNamespaces].sort();   // cluster-wide, not just loaded rows
        const filtered = items.filter(p =>
          (nsFilter === 'all' || p.namespace === nsFilter) &&
          p.name.toLowerCase().includes(nameFilter.toLowerCase())
        );

        const handleDelete = async (name, namespace) => {
          if (!window.confirm(`Delete PVC "${name}" in "${namespace}"?\n\nWARNING: This will delete the PVC. If a pod is using it, the pod may fail.`)) return;
          try {
            const r = await clustersAPI.exec(clusterId, `kubectl delete pvc ${name} -n ${namespace} --ignore-not-found=true`, { section: 'storage', action: 'delete' });
            assertExecOk(r, 'Delete');   // kubectl failures come back HTTP 200 on stderr
            refresh();
          } catch (e) { alert(`Delete failed: ${e.response?.data?.detail || e.message}`); }
        };

        return <>
          <FilterBar count={filtered.length} total={items.length} label="PVCs">
            <select value={nsFilter} onChange={e => setNsFilter(e.target.value)}>
              {namespaces.map(ns => <option key={ns} value={ns}>{ns === 'all' ? 'All namespaces' : ns}</option>)}
            </select>
            <input placeholder="Filter by name..." value={nameFilter} onChange={e => setNameFilter(e.target.value)} />
            {canCreate && (
            <button onClick={() => setShowCreate(true)} className="btn btn-primary btn-sm" style={{ marginLeft:'auto' }}>
              + Create PVC
            </button>
            )}
          </FilterBar>
          <DataTable cols={['Name', 'Namespace', 'Status', 'Capacity', 'Access Modes', 'Storage Class', 'Age', '']}
            rows={filtered.map(p => (
              <tr key={`${p.namespace}/${p.name}`}>
                <td><Code>{p.name}</Code></td>
                <td style={{ color: 'var(--ink-2)', fontSize: 13 }}>{p.namespace}</td>
                <td><StatusBadge status={p.status} /></td>
                <td style={{ fontWeight: 600 }}>{p.capacity}</td>
                <td style={{ fontSize: 12 }}>{p.access_modes}</td>
                <td style={{ fontSize: 12 }}>{p.storage_class}</td>
                <td><Age ts={p.created_at} /></td>
                <td>
                  {canDelete && (
                  <button onClick={() => handleDelete(p.name, p.namespace)} className="btn btn-tinted-danger btn-sm">
                    Delete
                  </button>
                  )}
                </td>
              </tr>
            ))} />
          {showCreate && (
            <CreatePVCModal
              clusterId={clusterId}
              initialNamespace={nsFilter !== 'all' ? nsFilter : 'default'}
              namespaces={allNamespaces}
              onClose={() => setShowCreate(false)}
              onCreated={() => { setShowCreate(false); refresh(); }}
            />
          )}
        </>;
      }}
    </LivePage>
  );
}
